/**
 * TrialExpiredModal — Blocking expiration dialog.
 *
 * Shows when the user's trial has expired. Cannot be dismissed — all tiers are
 * paid so the user must choose a plan to continue. The modal persists until
 * they navigate to billing and subscribe.
 */

import { useEffect, useMemo, useRef } from "react"
import { useNavigate } from "react-router"
import { Sparkles, Check } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { usePlanContext } from "@/lib/usePlanContext"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { trackEvent } from "@/lib/posthog"

const TRIAL_FEATURES = [
  "AI Performance Narratives",
  "Smart Weekly Actions",
  "AI Listing Audit",
  "Cross-Platform Review Analysis",
]

export function TrialExpiredModal() {
  const { status, degraded, plan, trialEndsAt } = usePlanContext()
  const { propertyId } = usePropertyContext()
  const navigate = useNavigate()

  const isExpired = useMemo(() => {
    if (status === "trial_expired") return true
    // Only show expiry modal when backend explicitly confirms no_subscription
    // and there's evidence of a past trial (trial_ends_at is in the past).
    // This avoids false positives from silent plan resolution failures.
    if (degraded && plan === "host" && status === "no_subscription" && trialEndsAt) {
      return new Date(trialEndsAt).getTime() < Date.now()
    }
    return false
  }, [status, degraded, plan, trialEndsAt])

  const shouldShow = isExpired && !!propertyId

  // Track when modal is shown (once)
  const tracked = useRef(false)
  useEffect(() => {
    if (shouldShow && !tracked.current) {
      tracked.current = true
      trackEvent("trial_expired_modal_shown", { property_id: propertyId })
    }
  }, [shouldShow, propertyId])

  return (
    <Dialog open={shouldShow}>
      <DialogContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="sm:max-w-md"
      >
        {/* Hidden accessible title */}
        <DialogTitle className="sr-only">Trial Expired</DialogTitle>
        <DialogDescription className="sr-only">
          Your Concierge trial has ended. Choose a plan to continue.
        </DialogDescription>

        <div className="flex flex-col items-center text-center py-2">
          <Sparkles className="h-8 w-8 text-primary mb-4" />

          <h2 className="text-xl font-display font-medium tracking-tight">
            Your Concierge trial has ended
          </h2>

          <p className="text-sm text-muted-foreground mt-2 mb-5">
            During your trial, you explored:
          </p>

          <ul className="space-y-2.5 w-full text-left mb-6">
            {TRIAL_FEATURES.map((feature) => (
              <li key={feature} className="flex items-center gap-2.5 text-sm text-foreground">
                <Check className="h-4 w-4 text-primary flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>

          <p className="text-sm text-muted-foreground mb-6">
            Choose a plan to continue using Stayframe.
          </p>

          <Button className="w-full" onClick={() => { trackEvent("trial_expired_cta_clicked", { property_id: propertyId, action: "upgrade" }); navigate("settings?tab=billing") }}>
            Choose a plan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
